import { useRouter } from 'next/navigation';

import { TradingLeaderboardRankingItem } from '@flipster/apis/v2/trading-leaderboards/types';
import { FormattedMessage } from '@flipster/i18n';
import Formatted from '@flipster/react/design-utils/formatted';
import Crown1StColorIcon from '@flipster/react/icon/generated/Crown1StColorIcon';
import Crown2NdColorIcon from '@flipster/react/icon/generated/Crown2NdColorIcon';
import Crown3RdColorIcon from '@flipster/react/icon/generated/Crown3RdColorIcon';
import LockClosedIcon from '@flipster/react/icon/generated/LockClosedIcon';
import { css } from '@flipster/styled-system/css';
import { center, hstack, stack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import segment from '@flipster/utils/segment';
import { TradingLeaderboardEvent } from '@flipster/utils/segment/events/trading-leaderboard';
import webviewHandler from '@flipster/utils/webview-handler';

import { CopyMasterBadge } from '../../shared/copy-trading/badge';
import { RankChangeToday } from '../shared/rank-change-today';

interface RankItemProps {
  ranking: TradingLeaderboardRankingItem;
  me?: { delta: number };
}

export const MyRankItemId = 'my-rank-item';
export const RankItem = ({ ranking, me }: RankItemProps) => {
  const router = useRouter();
  if (!ranking.rank) return;
  return (
    <li
      id={me && MyRankItemId}
      className={css(
        hstack.raw(),
        {
          position: 'relative',
          cursor: 'pointer',
          p: '16px 12px',
          gap: 12,
          rounded: 8,
          _hover: {
            backgroundColor: 'gray.white/10',
          },
          '& > *': { flexShrink: 0 },
          tablet: {
            p: '16px 24px',
            gap: 16,
          },
        },
        me && {
          bg: 'background.default.level-1',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'border.default.level-1',
        },
      )}
      onClick={() => {
        segment.track(TradingLeaderboardEvent.RankingPageRankerClick, {
          profile_id: ranking.userProfileId,
          clicked_user_name: ranking.nickname,
          clicked_ranking: ranking.rank ?? 0,
          is_influencer: ranking.isInfluencer,
        });
        webviewHandler.postMessage(
          {
            action: 'link',
            params: {
              page: `user/profile/${ranking.userProfileId}`,
            },
          },
          () => router.push(`/user/profile/${ranking.userProfileId}`),
        );
      }}
    >
      <span className={center({ w: 28, textStyle: 'Text/S/Monospace/Strong', color: 'content.default.level-2' })}>
        <Rank rank={ranking.rank} />
      </span>
      <RankProfile ranking={ranking} me={me} />
      <Formatted.Number
        value={ranking.pnl}
        withPlus
        withColor
        toFixed
        precision={2}
        css={{ textStyle: 'Text/L/Monospace/Regular', textAlign: 'right' }}
      />
    </li>
  );
};

interface RankProps {
  rank: number;
}
const Rank = ({ rank }: RankProps) => {
  return (
    [
      undefined,
      <Crown1StColorIcon key={rank} size={28} />,
      <Crown2NdColorIcon key={rank} size={28} />,
      <Crown3RdColorIcon key={rank} size={28} />,
    ][rank] ?? rank
  );
};

interface RankProfileProps {
  ranking: TradingLeaderboardRankingItem;
  me?: { delta: number };
}
const RankProfile = ({
  me,
  ranking: { nickname, isProfileOpen, isInfluencer, isCopyTradeMaster },
}: RankProfileProps) => {
  return (
    <span
      className={stack({
        gap: 2,
        flex: 1,
        overflow: 'hidden',
        cursor: 'pointer',
        textAlign: 'left',
        '& > span': {
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
      })}
    >
      <div className={hstack({ gap: 4 })}>
        <span className={css({ color: 'content.default.level-5', textStyle: 'Text/L/Strong' })}>{nickname}</span>
        {!isProfileOpen && <LockClosedIcon size={14} color={token('colors.content.default.level-1')} />}
        {me && (
          <RankChangeToday
            delta={me.delta}
            className={css({
              flexShrink: 0,
              tablet: {
                ml: 12,
              },
              tabletDown: {
                position: 'absolute',
                top: 0,
                left: '50%',
                transform: 'translate(-50%, -50%)',
              },
            })}
          />
        )}
      </div>
      <div className={css({ '& > :nth-child(n + 2)': { display: 'none' } })}>
        {isCopyTradeMaster && <CopyMasterBadge />}
        {isInfluencer && (
          <p className={css({ textStyle: 'Text/S/Regular', color: 'content.accent.promotion' })}>
            <FormattedMessage id='Autogen_common.r251126.s_Influencer' />
          </p>
        )}
      </div>
    </span>
  );
};
